import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import API from "../API";

const orderStatuses = {
  "-1": "İptal edildi",
  0: "Sürücü bekleniyor",
  1: "Sürücü bulundu",
  2: "Sürücü yolda",
  3: "Teslimat tarihi onaylandı",
  4: "Teslimat devam ediyor",
  10: "Tamamlandı",
  arrived_pickup: "İlk adrese varıldı",
  arrived_intermediate: "Ara durağa varıldı",
  arrived_delivery: "Teslimat adresine varıldı",
};
const reference = (id) =>
  String(id || "")
    .slice(-6)
    .toUpperCase();
export default function JyskRequestOperations({ requestId, onUpdated }) {
  const [request, setRequest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resolutions, setResolutions] = useState({});
  const [products, setProducts] = useState([]);
  const [note, setNote] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [notice, setNotice] = useState("");
  const call = async (path, body = {}) => {
    const result = await API.fetch(`jysk/${path}`, body);
    if (result.error) throw new Error(result.message || "İşlem tamamlanamadı.");
    return result;
  };
  const load = async () => {
    const data = await call("request", { id: requestId });
    setRequest(data.request);
    setProducts(data.request.barcodes || []);
  };
  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    run(load);
  }, [requestId]);
  const update = (extra) =>
    run(async () => {
      await call("update", { id: requestId, ...extra });
      await load();
      setNotice(extra.barcodes ? "Ürün değişiklikleri kaydedildi." : "Talep güncellendi.");
      onUpdated?.();
    });
  const issues = request?.productIssues || [];
  const open = issues.some((i) => i.status !== "resolved");
  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Typography variant="h6">JYSK teslimat operasyonu</Typography>
          {request &&
            !request.orderId &&
            request.status !== "order_created" && (
              <Stack direction="row" spacing={1}>
                {" "}
                <Button
                  variant="contained"
                  disabled={
                    busy ||
                    !request.paymentResponsibility ||
                    open ||
                    request.stockStatus !== "stock_arrived" ||
                    request.status === "cancelled"
                  }
                  onClick={() => {
                    const action = () =>
                      run(async () => {
                        const result = await call("convert", { id: requestId });
                        if (result.smsSent) {
                          setNotice(
                            "Sipariş bağlantısı için SMS gönderim işlemi tamamlandı. Gönderim durumunu SMS geçmişinden kontrol edin.",
                          );
                          return;
                        }
                        const destination = new URL(result.url);
                        const local = ["localhost", "127.0.0.1"].includes(
                          window.location.hostname,
                        );
                        const origin =
                          process.env.REACT_APP_JYSK_WEB_URL ||
                          (local
                            ? `${window.location.protocol}//${window.location.hostname}:3000`
                            : destination.origin);
                        window.open(
                          new URL(
                            destination.pathname + destination.search,
                            origin,
                          ).href,
                          "_blank",
                          "noopener,noreferrer",
                        );
                      });
                    if (request.paymentResponsibility === "customer")
                      setConfirmation({
                        message:
                          "Müşteriye SMS ile sipariş oluşturma bağlantısı gönderilecektir.",
                        label: "SMS gönder",
                        action,
                      });
                    else action();
                  }}
                >
                  {request.paymentResponsibility === "customer"
                    ? "Sipariş bağlantısı gönder"
                    : "Siparişe dönüştür"}
                </Button>
                <Button
                  color="error"
                  disabled={busy || request.status === "cancelled"}
                  onClick={() => {
                    if (window.confirm("Teslimat talebi iptal edilsin mi?"))
                      update({ cancel: true });
                  }}
                >
                  Talebi iptal et
                </Button>
              </Stack>
            )}
        </Stack>
        {notice && <Alert severity="info">{notice}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        {busy && <CircularProgress size={24} />}
        {request && (
          <>
            {!request.paymentResponsibility && (
              <Alert severity="warning">
                <Stack spacing={2}>
                  <Typography>
                    Eski talepte ödeme sorumluluğu belirtilmemiş. Sipariş
                    oluşturmadan önce doğrulayın.
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      disabled={busy}
                      onClick={() =>
                        update({ paymentResponsibility: "customer" })
                      }
                    >
                      Müşteri ödemeli
                    </Button>
                    <Button
                      disabled={busy}
                      onClick={() =>
                        update({ paymentResponsibility: "account" })
                      }
                    >
                      JYSK hesabı
                    </Button>
                  </Stack>
                </Stack>
              </Alert>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={
                  request.paymentResponsibility === "account"
                    ? "JYSK hesabı"
                    : "Müşteri ödemeli"
                }
              />
              <Chip
                label={
                  request.stockStatus === "stock_arrived"
                    ? "Stok geldi"
                    : "Stok bekleniyor"
                }
              />
              <Chip
                sx={
                  open
                    ? { bgcolor: "#ffcf85", color: "#593300", fontWeight: 700 }
                    : {}
                }
                color={open ? "warning" : "success"}
                label={
                  open ? "⚠ Ürün doğrulaması gerekli" : "Ürün kontrolü tamam"
                }
              />
              <Chip
                label={
                  request.orderId || request.status === "order_created"
                    ? "Sipariş oluşturuldu"
                    : "Sipariş oluşturulmadı"
                }
              />
            </Stack>
            <Box>
              <Typography fontWeight={600}>
                Orijinal mağaza seçimleri
              </Typography>
              <Typography sx={{ mt: 1 }}>
                {request.linkReason === "sale" ? "Satış" : "Müşteri siparişi"} ·{" "}
                {{ immediate: "Hemen", scheduled: "Planlı", flexible: "Esnek" }[
                  request.service
                ] || "Hizmet daha sonra seçilecek"}
              </Typography>
              <Typography>
                {request.employee ? (
                  <>
                    Çalışan: {[request.employee.id, request.employee.name].filter(Boolean).join(" · ")}
                  </>
                ) : (
                  <>
                    Gönderen hesap: {request.sender?.name || "—"} ·{" "}
                    {request.sender?.email || "—"}
                  </>
                )}
              </Typography>
              <Typography>
                Sipariş türü: {{ customerProductReplacement: "Müşteri ürün değişimi", returnInterstoreTransfer: "İade / mağazalar arası transfer", promotion: "Kampanya", newStoreApprovedShipment: "Yeni mağaza onaylı gönderi", other: "Diğer" }[request.orderPurpose] || request.orderPurpose || "Belirtilmemiş"}
              </Typography>
              <Typography>
                Mağaza notu: {request.internalNote || "—"}
              </Typography>
              <Typography>
                İç taşıma tercihi:{" "}
                {request.internalCarrying === "apartment"
                  ? "Daireye taşıma talebi var"
                  : "Araçtan teslim"}
              </Typography>
            </Box>
            {!!request.productReviews?.length && (
              <Box component="details">
                <summary>Orijinal müşteri kontrolleri</summary>
                {request.productReviews.map((review, i) => (
                  <Typography key={i}>
                    {review.barcode} · {review.quantity} adet ·{" "}
                    {review.confirmed
                      ? "Müşteri onayladı"
                      : "Müşteri sorun bildirdi"}
                  </Typography>
                ))}
              </Box>
            )}
            {products.map((p, index) => (
              <Paper
                variant="outlined"
                sx={{ p: 2, borderRadius: 2 }}
                key={index}
              >
                <Stack spacing={2}>
                  <img
                    src={`https://cdn1.jysk.com/getimage/wd3.medium/${encodeURIComponent(p.barcode)}`}
                    alt={p.product_name || p.barcode}
                    width={72}
                    height={72}
                    style={{ objectFit: "contain" }}
                    onError={(e) => {
                      e.currentTarget.style.visibility = "hidden";
                    }}
                  />
                  <Typography fontWeight={600}>
                    {p.barcode} · {p.product_name}
                  </Typography>
                  <TextField
                    label="Doğrulanmış ürün kodu"
                    size="small"
                    value={p.barcode}
                    inputProps={{ maxLength: 7 }}
                    disabled={
                      !!request.orderId ||
                      request.status === "order_created" ||
                      busy
                    }
                    onChange={(e) =>
                      setProducts((list) =>
                        list.map((item, i) =>
                          i === index
                            ? { ...item, barcode: e.target.value }
                            : item,
                        ),
                      )
                    }
                  />
                  <TextField
                    label="Doğrulanmış adet"
                    type="number"
                    size="small"
                    value={p.quantity}
                    inputProps={{ min: 1, max: 99 }}
                    disabled={
                      !!request.orderId ||
                      request.status === "order_created" ||
                      busy
                    }
                    onChange={(e) =>
                      setProducts((list) =>
                        list.map((item, i) =>
                          i === index
                            ? { ...item, quantity: Number(e.target.value) }
                            : item,
                        ),
                      )
                    }
                  />
                </Stack>
              </Paper>
            ))}
            {issues.map((issue) => (
              <Alert
                key={issue.id}
                severity={issue.status === "resolved" ? "success" : "warning"}
              >
                <Stack spacing={2}>
                  <Typography>{issue.explanation}</Typography>
                  <Typography variant="body2">
                    Müşterinin bildirdiği orijinal ürün:{" "}
                    {issue.originalProduct?.barcode} ·{" "}
                    {issue.originalProduct?.quantity} adet
                  </Typography>
                  {issue.status === "resolved" ? (
                    <Typography>Çözüm: {issue.resolution}</Typography>
                  ) : (
                    <>
                      <TextField
                        label="Doğrulama / düzeltme açıklaması"
                        multiline
                        minRows={2}
                        value={resolutions[issue.id] || ""}
                        onChange={(e) =>
                          setResolutions((v) => ({
                            ...v,
                            [issue.id]: e.target.value,
                          }))
                        }
                      />
                      <Button
                        variant="outlined"
                        disabled={busy || !resolutions[issue.id]?.trim()}
                        onClick={() =>
                          update({
                            resolveIssue: issue.id,
                            resolution: resolutions[issue.id],
                            barcodes: products,
                          })
                        }
                      >
                        Ürünü düzelt ve bildirimi çöz
                      </Button>
                    </>
                  )}
                </Stack>
              </Alert>
            ))}
            {!request.orderId && request.status !== "order_created" && (
              <Button
                variant="outlined"
                disabled={busy || request.status === "cancelled"}
                onClick={() => update({ barcodes: products })}
              >
                Ürünleri kaydet
              </Button>
            )}
            <Typography fontWeight={600}>Mağazanın eklediği fişler</Typography>
            <Stack spacing={1}>
              {(request.receipt_images || []).map((f, i) => (
                <Link
                  key={i}
                  href={f.url || `data:${f.mime_type};base64,${f.base64}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {f.name || `Fiş ${i + 1}`}
                </Link>
              ))}
              {!request.receipt_images?.length && (
                <Typography color="text.secondary">Fiş eklenmemiş.</Typography>
              )}
            </Stack>
            {request.orderId ? (
              <Button href={`/order/${request.orderId}`} variant="contained">
                Sipariş #{reference(request.orderId)} ·{" "}
                {orderStatuses[request.linkedOrderStatus] ||
                  "Durum bilgisi bekleniyor"}{" "}
                · Aç
              </Button>
            ) : request.status === "order_created" ? (
              <Alert severity="warning">
                Bu eski talep daha önce siparişe dönüştürülmüş. Sipariş
                bağlantısını veri mutabakatı ile doğrulayın.
              </Alert>
            ) : (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="outlined"
                  disabled={busy || request.status === "cancelled"}
                  onClick={() => {
                    const stockStatus =
                      request.stockStatus === "stock_arrived"
                        ? "stock_pending"
                        : "stock_arrived";
                    setConfirmation({
                      message: `Stok durumu '${stockStatus === "stock_arrived" ? "Stok geldi" : "Stok bekleniyor"}' olarak değiştirilecektir. Devam etmek istiyor musunuz?`,
                      label: "Onayla",
                      action: () => update({ stockStatus }),
                    });
                  }}
                >
                  {request.stockStatus === "stock_arrived"
                    ? "Stok bekleniyor olarak işaretle"
                    : "Stok geldi olarak işaretle"}
                </Button>
              </Stack>
            )}
            {request.paymentResponsibility === "customer" && (
              <Alert severity="info">
                {request.linkReason === "sale"
                  ? "Müşteri formu ve ürün kontrolü tamamlandıktan sonra üstteki Sipariş bağlantısı gönder düğmesini kullanın. Stok durumunu değiştirmeniz gerekmez."
                  : "Stok ve ürün kontrolü tamamlandığında müşteriye online sipariş bağlantısı otomatik gönderilir. Aynı mesaj tekrar gönderilmez."}
              </Alert>
            )}
            <Box>
              <Typography fontWeight={600}>Operasyon notları</Typography>
              {(request.operationsNotes || []).map((entry, i) => (
                <Box key={i} sx={{ my: 2 }}>
                  <Typography sx={{ whiteSpace: "pre-wrap" }}>
                    {entry.text}
                  </Typography>
                  <Typography variant="caption">
                    {entry.author?.name || entry.author?.email || "Operasyon"} ·{" "}
                    {new Date(entry.at).toLocaleString("tr-TR")}
                  </Typography>
                </Box>
              ))}
              {!request.orderId &&
                !["order_created", "cancelled"].includes(request.status) && (
                  <Stack spacing={1}>
                    <TextField
                      label="Yeni operasyon notu"
                      multiline
                      minRows={2}
                      value={note}
                      inputProps={{ maxLength: 3000 }}
                      onChange={(e) => setNote(e.target.value)}
                    />
                    <Button
                      disabled={busy || !note.trim()}
                      onClick={() =>
                        run(async () => {
                          await call("note", { id: requestId, text: note });
                          setNote("");
                          await load();
                        })
                      }
                    >
                      Notu kaydet
                    </Button>
                  </Stack>
                )}
            </Box>
            {!!request.history?.length && (
              <Box>
                <Typography fontWeight={600}>Değişiklik geçmişi</Typography>
                {request.history.map((h, i) => (
                  <Box component="details" key={i} sx={{ mt: 2 }}>
                    <summary>
                      {new Date(h.at).toLocaleString("tr-TR")} ·{" "}
                      {h.actorName || "Operasyon"}
                    </summary>
                    {h.changes?.stockStatus && <Typography variant="body2">Stok durumu: {h.changes.stockStatus === "stock_arrived" ? "Stok geldi" : "Stok bekleniyor"}</Typography>}
                    {h.changes?.status === "cancelled" && <Typography variant="body2">Talep iptal edildi: {h.changes.cancelReason || "Açıklama girilmedi"}</Typography>}
                    {h.changes?.productIssues?.filter(issue => issue.status === "resolved").map(issue => <Typography key={issue.id} variant="body2">Ürün doğrulaması: {issue.barcode} · {issue.resolution}</Typography>)}
                    <Typography variant="body2">
                      Önceki ürünler:{" "}
                      {(h.before?.barcodes || [])
                        .map((p) => `${p.barcode}: ${p.quantity}`)
                        .join(", ")}
                    </Typography>
                    <Typography variant="body2">
                      Yeni ürünler:{" "}
                      {(h.changes?.barcodes || [])
                        .map((p) => `${p.barcode}: ${p.quantity}`)
                        .join(", ") || "Değişmedi"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </Stack>
      <Dialog
        open={!!confirmation}
        onClose={() => !busy && setConfirmation(null)}
      >
        <DialogTitle>İşlemi onaylayın</DialogTitle>
        <DialogContent>{confirmation?.message}</DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setConfirmation(null)}>
            Vazgeç
          </Button>
          <Button
            disabled={busy}
            onClick={() => {
              const action = confirmation.action;
              setConfirmation(null);
              action();
            }}
          >
            {confirmation?.label}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
